import { OFFER_API_BASE } from "@/config/apiConfig";
import { apiFetch } from "@/services/apiService";
import { publicAuthorizedFetch } from "@/services/publicAuthorizedFetch";
import { languageUtils } from "@/utils/languageUtils";
import { FIND_RIDE_API_PATHS } from "../constants/findRide.constants";
import { buildFindRideQuery } from "../utils/buildFindRideQuery";
import type { LaterRideRequestBody } from "../utils/buildLaterRidePayload";
import type { FindRideSearchCriteria } from "../types/findRide.types";

type FindRideJsonHeaders = {
  Accept: string;
  lang: string;
  "Content-Type"?: string;
};

/**
 * Low-level HTTP for Find Ride search / later-notify.
 * Search uses {@link publicAuthorizedFetch} (user session or in-memory guest token).
 * Later-notify uses {@link apiFetch} (signed-in user only).
 */
export class FindRideApiService {
  constructor(private readonly baseUrl: string = OFFER_API_BASE) {}

  private buildUrl(path: string, query?: URLSearchParams): string {
    const root = `${this.baseUrl}${path}`;
    return query ? `${root}?${query.toString()}` : root;
  }

  private jsonHeaders(includeContentType = false): FindRideJsonHeaders {
    const headers: FindRideJsonHeaders = {
      Accept: "application/json",
      lang: languageUtils.getCurrentLanguage(),
    };
    if (includeContentType) {
      headers["Content-Type"] = "application/json";
    }
    return headers;
  }

  private async readErrorMessage(
    response: Response,
    fallback: string
  ): Promise<string> {
    const text = await response.text().catch(() => "");
    return text || fallback;
  }

  async searchRides(criteria: FindRideSearchCriteria): Promise<unknown> {
    const url = this.buildUrl(
      FIND_RIDE_API_PATHS.search,
      buildFindRideQuery(criteria)
    );

    const response = await publicAuthorizedFetch(url, {
      method: "GET",
      headers: this.jsonHeaders(),
    });

    if (!response.ok) {
      throw new Error(
        await this.readErrorMessage(
          response,
          `Ride search failed (${response.status})`
        )
      );
    }

    return response.json();
  }

  /**
   * Save search for later notify — POST /laterrides
   * Requires a signed-in user session (not guest token).
   */
  async saveLaterRide(body: LaterRideRequestBody): Promise<void> {
    const url = this.buildUrl(FIND_RIDE_API_PATHS.laterRides);

    const response = await apiFetch(url, {
      method: "POST",
      headers: this.jsonHeaders(true),
      body: JSON.stringify(body),
    });

    if (response.status !== 200) {
      throw new Error(
        await this.readErrorMessage(
          response,
          `Unable to save notify request (${response.status})`
        )
      );
    }
  }
}

export const findRideApiService = new FindRideApiService();
